/*
 * Console cursor: arrow keys move focus between tiles (`[data-tile]`) inside
 * a container, like a gamepad d-pad. Picks the nearest tile in the pressed
 * direction by centre distance, so bento grids and channels both work.
 * Enter/Space activate the focused tile (native for links/buttons).
 * Hovering a tile also focuses it so mouse and keyboard share one cursor.
 */
import { onBeforeUnmount, onMounted, type Ref } from 'vue';

type Dir = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';
const DIRS: readonly Dir[] = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

function centre(el: Element) {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function nearest(from: HTMLElement, tiles: HTMLElement[], dir: Dir): HTMLElement | null {
  const a = centre(from);
  let best: { el: HTMLElement; score: number } | null = null;
  for (const el of tiles) {
    if (el === from) continue;
    const b = centre(el);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const forward =
      dir === 'ArrowRight' ? dx : dir === 'ArrowLeft' ? -dx : dir === 'ArrowDown' ? dy : -dy;
    if (forward <= 8) continue;
    const sideways = dir === 'ArrowLeft' || dir === 'ArrowRight' ? Math.abs(dy) : Math.abs(dx);
    // Favour tiles in line with the cursor; sideways drift costs double.
    const score = forward + sideways * 2;
    if (!best || score < best.score) best = { el, score };
  }
  return best?.el ?? null;
}

export function useFocusCursor(container: Ref<HTMLElement | null>) {
  function tiles(): HTMLElement[] {
    return container.value ? [...container.value.querySelectorAll<HTMLElement>('[data-tile]')] : [];
  }

  function onKeydown(e: KeyboardEvent) {
    // A nested cursor (a channel inside the page) already moved focus.
    if (e.defaultPrevented) return;
    if (!DIRS.includes(e.key as Dir)) return;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !active.hasAttribute('data-tile')) return;
    if (!container.value?.contains(active)) return;
    const next = nearest(active, tiles(), e.key as Dir);
    if (!next) return;
    e.preventDefault();
    next.focus({ preventScroll: false });
  }

  function onPointerOver(e: PointerEvent) {
    const tile = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-tile]');
    if (tile && document.activeElement !== tile && !tile.contains(document.activeElement)) {
      tile.focus({ preventScroll: true });
    }
  }

  onMounted(() => {
    container.value?.addEventListener('keydown', onKeydown);
    container.value?.addEventListener('pointerover', onPointerOver);
  });
  onBeforeUnmount(() => {
    container.value?.removeEventListener('keydown', onKeydown);
    container.value?.removeEventListener('pointerover', onPointerOver);
  });
}
